import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  ListGroup,
  ListGroupItem,
} from 'reactstrap';

import ErrorBoundary from '../ErrorBoundary';

import ShareModalView from '../Share/ModalView';
import ShareModalIncludeFutureFilesSwitch from '../Share/ModalIncludeFutureFilesSwitch';
import ShareModalAllowDownload from '../Share/ModalAllowDownload';
import ShareModalDownloadReasons from '../Share/ModalDownloadReasons';
import ShareModalWatermark from '../Share/ModalWatermark';
import ShareModalRequireLogin from '../Share/ModalRequireLogin';
import ShareModalAllowComments from '../Share/ModalAllowComments';
import ShareModalShowPeople from '../Share/ModalShowPeople';
import ShareModalAllowFavoriting from '../Share/ModalAllowFavoriting';
import ShareModalAllowVersions from '../Share/ModalAllowVersions';
import ShareModalHideFileDetails from '../Share/ModalHideFileDetails';
import ShareModalStatusChange from '../Share/ModalStatusChange';
import ShareModalSelectFileSizes from '../Share/ModalSelectFileSizes';
import ShareModalRequireName from '../Share/ModalRequireName';
import ShareModalRequireEmail from '../Share/ModalRequireEmail';
import ShareModalColor from '../Share/ModalColor';
import ShareModalLogo from '../Share/ModalLogo';

import { getShareOptionValue } from '../../helpers/Share';
import { getSetting } from '../../helpers/Settings';

const AdminShareSelectedTemplate = (props) => {
  const {
    handleShareTemplatesChange,
    handleSelectedTemplateUuid,
    selectedTemplateUuid,
    client,
    templates,
  } = props;

  const selectedTemplate = templates.length > 0 ?
    templates.find(p => p.uuid === selectedTemplateUuid) :
    null;

  const [logoUrl, setLogoUrl] = useState(getShareOptionValue('logo_url', selectedTemplate));

  useEffect(() => {
    if (selectedTemplateUuid === null && templates.length > 0) {
      handleSelectedTemplateUuid(templates[0].uuid);
    }
  }, [handleSelectedTemplateUuid, selectedTemplateUuid, templates]);

  useEffect(() => {
    const nextLogoUrl = getShareOptionValue('logo_url', selectedTemplate);
    const nextLogo = getShareOptionValue('logo', selectedTemplate);

    setLogoUrl((prevLogoUrl) => {
      if (nextLogoUrl) {
        return nextLogoUrl;
      }
      if (!nextLogo) {
        return null;
      }
      return prevLogoUrl;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate.uuid]);

  const downloadReasonsAvailable = getSetting('download_reasons').length > 0;

  return (
    <ErrorBoundary>
      <ListGroup flush className="switch-list list-group-item-share-setting">
        <ListGroupItem>
          <ShareModalColor
            color={getShareOptionValue('color', selectedTemplate)}
            handleChange={(color) => {
              handleShareTemplatesChange(selectedTemplate.uuid, 'color', color);
            }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalLogo
            logo={getShareOptionValue('logo', selectedTemplate)}
            logoUrl={logoUrl}
            handleChange={(url) => {
              handleShareTemplatesChange(selectedTemplate.uuid, 'logo', url);
            }}
            handleChangeLogoUrl={(url) => {
              setLogoUrl(url);
              handleShareTemplatesChange(selectedTemplate.uuid, 'logo_url', url);
            }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalView
            view={getShareOptionValue('view', selectedTemplate)}
            handleChangeView={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'view', e.target.value); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalHideFileDetails
            hideFileDetails={getShareOptionValue('hide_file_details', selectedTemplate)}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'hide_file_details', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalIncludeFutureFilesSwitch
            includeFutureFiles={getShareOptionValue('include_future_files', selectedTemplate)}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'include_future_files', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalAllowDownload
            allowDownload={getShareOptionValue('allow_download', selectedTemplate)}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'allow_download', e.target.checked); }}
            userCanDownload={true}
            hasRestrictedFiles={false}
          />
        </ListGroupItem>
        {downloadReasonsAvailable && (
          <ListGroupItem>
            <ShareModalDownloadReasons
              allowDownload={getShareOptionValue('allow_download', selectedTemplate)}
              downloadReasons={getShareOptionValue('download_reasons', selectedTemplate)}
              handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'download_reasons', e.target.checked); }}
              userCanDownload={true}
              hasRestrictedFiles={false}
            />
          </ListGroupItem>
        )}
        <ListGroupItem>
          <ShareModalRequireName
            requireName={getShareOptionValue('require_name', selectedTemplate)}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'require_name', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalRequireEmail
            requireEmail={getShareOptionValue('require_email', selectedTemplate)}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'require_email', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalWatermark
            watermark={getShareOptionValue('watermark', selectedTemplate)}
            watermarkEnabled={!!client.watermark_url}
            userCanDownload={true}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'watermark', e.target.checked); }}
            handleClose={() => {}}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalRequireLogin
            linkRequireLogin={getShareOptionValue('login_required', selectedTemplate)}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'login_required', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalShowPeople
            showPeople={getShareOptionValue('show_people', selectedTemplate)}
            hideFileDetails={getShareOptionValue('hide_file_details', selectedTemplate)}
            handleChangeShowPeople={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'show_people', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalAllowComments
            allowComments={getShareOptionValue('allow_comments', selectedTemplate)}
            hideFileDetails={getShareOptionValue('hide_file_details', selectedTemplate)}
            handleChangeAllowComments={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'allow_comments', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalAllowFavoriting
            allowFavoriting={getShareOptionValue('allow_favoriting', selectedTemplate)}
            handleChangeAllowFavoriting={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'allow_favoriting', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalAllowVersions
            allowVersions={getShareOptionValue('allow_versions', selectedTemplate)}
            handleChangeAllowVersions={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'allow_versions', e.target.checked); }}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalStatusChange
            statusChange={getShareOptionValue('status_change', selectedTemplate)}
            handleChangeStatusChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'status_change', e.target.checked); }}
            handleClose={() => {}}
          />
        </ListGroupItem>
        <ListGroupItem>
          <ShareModalSelectFileSizes
            fileSizes={getShareOptionValue('file_sizes', selectedTemplate)}
            selectFileSizes={selectedTemplate && 'select_file_sizes' in selectedTemplate ? selectedTemplate.select_file_sizes : false}
            allowDownload={selectedTemplate && 'allow_download' in selectedTemplate ? selectedTemplate.allow_download : true}
            handleChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'select_file_sizes', e.target.checked); }}
            handleFileSizesChange={(e) => { handleShareTemplatesChange(selectedTemplate.uuid, 'file_sizes', { checked: e.target.checked, value: e.target.value }); }}
          />
        </ListGroupItem>
      </ListGroup>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
});

AdminShareSelectedTemplate.propTypes = {
  client: PropTypes.object.isRequired,
  selectedTemplateUuid: PropTypes.string,
  templates: PropTypes.array.isRequired,
  handleShareTemplatesChange: PropTypes.func.isRequired,
  handleSelectedTemplateUuid: PropTypes.func.isRequired,
};

AdminShareSelectedTemplate.defaultProps = {
  selectedTemplateUuid: null,
};

export default connect(mapStateToProps)(AdminShareSelectedTemplate);
